import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2, XCircle, Home } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/payments`;

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 8;

const DonationSuccess = () => {
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'polling', payment_status: '', amount: 0, currency: 'MYR' });
  const [attempts, setAttempts] = useState(0);

  const sessionId = new URLSearchParams(window.location.search).get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setState({ status: 'error', payment_status: 'missing_session' });
      return;
    }

    let cancelled = false;

    const poll = async (n) => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/status/${sessionId}`);
        const data = res.data;
        setState(data);

        if (data.payment_status === 'paid') return;
        if (data.status === 'expired') return;
        if (n >= MAX_ATTEMPTS) return;

        setAttempts(n + 1);
        setTimeout(() => poll(n + 1), POLL_INTERVAL);
      } catch (err) {
        setState({ status: 'error', payment_status: 'fetch_failed' });
      }
    };

    poll(0);
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const paid = state.payment_status === 'paid';
  const expired = state.status === 'expired';
  const polling = !paid && !expired && state.status !== 'error';

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF8F3] to-white flex items-center justify-center p-4">
      <Card className="max-w-lg w-full border-[#D4AF37]/30 shadow-xl" data-testid="donation-success-card">
        <CardContent className="p-10 text-center">
          {polling && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-[#1565C0] animate-spin mb-6" />
              <h1 className="text-2xl font-serif text-[#0A1628] mb-2">Confirming your donation…</h1>
              <p className="text-sm text-gray-600">
                Please don't close this page. ({attempts}/{MAX_ATTEMPTS} checks)
              </p>
            </>
          )}

          {paid && (
            <>
              <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500 mb-6" />
              <h1 className="text-3xl font-serif text-[#0A1628] mb-2" data-testid="donation-success-heading">
                Thank you for your <span className="italic text-[#D4AF37]">generosity</span>!
              </h1>
              <p className="text-gray-600 mb-4">
                Your donation of <strong className="text-[#D4AF37]">
                  {state.currency?.toUpperCase()} {(state.amount || 0).toLocaleString()}
                </strong> has been received.
              </p>
              <p className="text-sm text-gray-500 mb-8">
                A receipt will be sent to your email shortly. Every ringgit goes directly to EWRF's education,
                counselling, and welfare programmes.
              </p>
              <Button onClick={() => navigate('/')} className="bg-[#1565C0]" data-testid="back-home-btn">
                <Home className="w-4 h-4 mr-2" /> Back to homepage
              </Button>
            </>
          )}

          {expired && (
            <>
              <XCircle className="w-16 h-16 mx-auto text-amber-500 mb-6" />
              <h1 className="text-2xl font-serif text-[#0A1628] mb-2">Checkout session expired</h1>
              <p className="text-gray-600 mb-6">No worries — you can try again whenever you're ready.</p>
              <Button onClick={() => navigate('/#donate')} className="bg-[#1565C0]" data-testid="back-home-btn">
                Try again
              </Button>
            </>
          )}

          {state.status === 'error' && (
            <>
              <XCircle className="w-16 h-16 mx-auto text-red-500 mb-6" />
              <h1 className="text-2xl font-serif text-[#0A1628] mb-2">We couldn't verify your payment</h1>
              <p className="text-gray-600 mb-6">
                If the charge appears on your card, please email{' '}
                <a className="text-[#1565C0] underline" href="mailto:admin@ewrf.org.my">admin@ewrf.org.my</a>
                {' '}with this session ID:
                <br />
                <code className="text-xs">{sessionId}</code>
              </p>
              <Button onClick={() => navigate('/')} variant="outline" data-testid="back-home-btn">
                <Home className="w-4 h-4 mr-2" /> Back to homepage
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DonationSuccess;
